/**
 * Collapse Component - Theme-aware collapsible panels
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;
const { useState } = React;

import { styles } from '../styles';

// ✅ Fix TypeScript error: Declare React types for type checking
type ReactNode = any;
type CSSProperties = any;

export interface CollapsePanelProps {
  header: ReactNode;
  key: string;
  children?: ReactNode;
  disabled?: boolean;
  showArrow?: boolean;
}

export interface CollapseProps {
  activeKey?: string | string[];
  defaultActiveKey?: string | string[];
  onChange?: (activeKey: string | string[]) => void;
  accordion?: boolean; // Only one panel open at a time
  children?: ReactNode;
  style?: CSSProperties;
}

export function CollapsePanel({ header, key, children, disabled, showArrow = true }: CollapsePanelProps) {
  // Panel is just a container - actual rendering happens in Collapse
  return null;
}

export function Collapse({
  activeKey: controlledActiveKey,
  defaultActiveKey,
  onChange,
  accordion = false,
  children,
  style,
}: CollapseProps) {
  // ✅ Fix TypeScript error: Explicitly type useState without type arguments
  const initialValue: string | string[] = controlledActiveKey !== undefined 
    ? controlledActiveKey 
    : (defaultActiveKey || (accordion ? '' : []));
  const [internalActiveKey, setInternalActiveKey] = (useState as any)(initialValue);

  // Extract Panel children
  const panels: CollapsePanelProps[] = [];
  React.Children.forEach(children, (child: any) => {
    if (React.isValidElement(child) && (child.type === CollapsePanel || (child.type as any)?.displayName === 'CollapsePanel')) {
      panels.push({
        header: child.props.header,
        key: child.props.key || child.key,
        children: child.props.children,
        disabled: child.props.disabled,
        showArrow: child.props.showArrow,
      });
    }
  });

  const activeKeys = Array.isArray(internalActiveKey) ? internalActiveKey : (internalActiveKey ? [internalActiveKey] : []);
  const controlledActiveKeys = controlledActiveKey !== undefined 
    ? (Array.isArray(controlledActiveKey) ? controlledActiveKey : [controlledActiveKey])
    : null;

  const isActive = (key: string) => {
    const keys = controlledActiveKeys || activeKeys;
    return keys.includes(key);
  };

  const handleToggle = (key: string) => {
    if (controlledActiveKey === undefined) {
      if (accordion) {
        setInternalActiveKey(isActive(key) ? '' : key);
        onChange?.(isActive(key) ? '' : key);
      } else {
        const newKeys = isActive(key)
          ? activeKeys.filter(k => k !== key)
          : [...activeKeys, key];
        setInternalActiveKey(newKeys);
        onChange?.(newKeys);
      }
    } else {
      if (accordion) {
        onChange?.(isActive(key) ? '' : key);
      } else {
        const currentKeys = controlledActiveKeys || [];
        const newKeys = isActive(key)
          ? currentKeys.filter((k: string) => k !== key)
          : [...currentKeys, key];
        onChange?.(newKeys);
      }
    }
  };

  const containerStyle: CSSProperties = {
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'var(--card-bg)',
    ...style,
  };

  return React.createElement('div', { style: containerStyle },
    panels.map((panel) => {
      const active = isActive(panel.key);
      
      return React.createElement('div', {
        key: panel.key,
        style: {
          borderBottom: '1px solid var(--border-color)',
        }
      },
        React.createElement('button', {
          onClick: () => !panel.disabled && handleToggle(panel.key),
          disabled: panel.disabled,
          style: {
            width: '100%',
            padding: '12px 16px',
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: panel.disabled ? 'not-allowed' : 'pointer',
            color: 'var(--main-text)',
            opacity: panel.disabled ? 0.5 : 1,
          },
          'aria-expanded': active,
          'aria-controls': `collapse-content-${panel.key}`,
        },
          React.createElement('span', { style: { fontWeight: '500' } }, panel.header),
          (panel.showArrow !== false && panel.showArrow !== undefined) && React.createElement('span', {
            style: {
              transform: active ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              marginLeft: '8px',
            }
          }, '▶')
        ),
        active && React.createElement('div', {
          id: `collapse-content-${panel.key}`,
          style: {
            padding: '12px 16px',
            borderTop: '1px solid var(--border-color)',
            background: 'var(--input-bg)',
          }
        }, panel.children)
      );
    })
  );
}

// Attach Panel to Collapse
(Collapse as any).Panel = CollapsePanel;

